<template>
  <div v-if="sortedChips.length" class="relative flex items-center border-0 border-grey-300 im:border-l border-solid h-full overflow-hidden">
    <div
      class="left-0 absolute flex justify-center items-center bg-white mb-0.5 im:mb-0 border-0 border-grey-300 border-r border-solid w-8 h-full transition-all duration-300 cursor-pointer"
      :class="{
        'collapse opacity-0': firstChipIsVisible,
        'delay-500': !firstChipIsVisible,
      }"
      @click="scroll(-1)"
    >
      <div class="flex justify-center items-center w-8">
        <ChevronLeftIcon class="w-3 h-3" />
      </div>
    </div>
    <BaseChipSelector
      ref="chipsElement"
      v-model="selectedChipList"
      class="select-none no-scrollbar pb-px"
      additional-item-classes="font-semibold"
      :options="sortedChips"
      @change="onChangeChip"
    >
      <template #before>
        <div ref="firstChip" class="min-w-px" />
      </template>
      <template #after>
        <div ref="lastChip" class="min-w-px" />
      </template>
    </BaseChipSelector>

    <div class="right-0 absolute flex justify-center items-center bg-white mb-0.5 im:mb-0 border-0 border-grey-300 border-l border-solid w-8 h-full transition-all duration-300 cursor-pointer" :class="{ 'collapse opacity-0': lastChipIsVisible}" @click="scroll(1)">
      <div class="flex justify-center items-center w-8">
        <ChevronRightIcon class="w-3 h-3" />
      </div>
    </div>
  </div>
</template>

<script setup>
import ChevronLeftIcon from '@tagvenue/icons/24/bold/arrows/chevron-left.svg';
import ChevronRightIcon from '@tagvenue/icons/24/bold/arrows/chevron-right.svg';
import ChristmasIcon from '@tagvenue/icons/24/christmas.svg';
import { useEventListener, useIntersectionObserver } from '@vueuse/core';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';

import BaseChipSelector from '@/components/Ui/Forms/BaseChipSelector.vue';
import { eventTypeChipSlug } from '@/modules/public/search/services/event-type-chips';
import { quickFilterLabel } from '@/modules/public/search/services/quick-filter-label';

const chipsElement = ref(null);
const firstChip = ref(null);
const lastChip = ref(null);

const store = useStore();

const selectedChipList = ref([]);
const updatedByChip = ref(false);

const firstChipIsVisible = ref(true);
const lastChipIsVisible = ref(true);

const chipsList = ref([]);

const CHRISTMAS_ROOM_TAG_IDS = [101, 103]; // christmas-dinner, christmas-party (RoomTagEntity ids)

const isChristmasChip = (chip) => chip.type === 'room_tags'
  && chip.values.some((value) => CHRISTMAS_ROOM_TAG_IDS.includes(+value));

// value stays derived from the original backend label: it is the stable selection/
// tracking key and must not collide with a real "Holiday Party" chip.
const toChipValue = (quickFilter) => quickFilter.label.toLowerCase().split(' ').join('_');

// The new event type's own row lists neither the chip that switched to it nor the chips carried over from the
// previous search, so they are added in front.
const quickFilters = computed(() => {
  const leadingChips = [store.getters.activeEventTypeChip, ...store.state.carriedQuickFilters].filter(Boolean);
  const leadingLabels = leadingChips.map((chip) => chip.label);

  return [...leadingChips, ...store.state.quickFilters.filter((chip) => !leadingLabels.includes(chip.label))];
});

// Selected only to show which chip switched the event type – it never adds a filter.
const activeEventTypeChipValue = computed(() => {
  const { activeEventTypeChip } = store.getters;
  return activeEventTypeChip ? toChipValue(activeEventTypeChip) : null;
});

// Every other selected chip goes along, so it stays selected in the row of the new event type.
const switchEventType = (eventTypeSlug, selectedChipValues) => {
  const carriedQuickFilters = quickFilters.value.filter((quickFilter) => {
    const chipValue = toChipValue(quickFilter);
    return selectedChipValues.includes(chipValue) && chipValue !== activeEventTypeChipValue.value;
  });

  store.dispatch('reloadSearchWithEventType', { eventTypeSlug, carriedQuickFilters });
};

const dropCarriedChip = (chipValue) => {
  const { carriedQuickFilters } = store.state;
  if (carriedQuickFilters.some((quickFilter) => toChipValue(quickFilter) === chipValue)) {
    store.commit(
      'setCarriedQuickFilters',
      carriedQuickFilters.filter((quickFilter) => toChipValue(quickFilter) !== chipValue),
    );
  }
};

const chips = computed(() => {
  return quickFilters.value.map((chip) => {
    return {
      ...chip,
      label: quickFilterLabel(chip, store.state.l10n.ISOCountryCode),
      value: toChipValue(chip),
      ...(isChristmasChip(chip) && {
        iconComponent: ChristmasIcon,
        additionalIconClasses: 'text-green-700',
      }),
    };
  });
});

const combinedVenuePartChip = computed(() => {
  return chips.value.find((chip) => chip.type === 'venue_part' && chip.values.length > 1);
});

// The combined "Private space" chip broadens results to several area types, but its selected
// state tracks only its primary area type (private indoor space — the first of its values).
const combinedPrimaryValue = computed(() => combinedVenuePartChip.value?.values[0]);

// Once every area type the combined chip covers is selected, the combined chip stands in for
// its siblings, so the "Private outdoor space" and "Whole venue" chips are hidden to avoid
// implying they were selected without client interaction.
const combinedFullySelected = computed(() => {
  const combinedChip = combinedVenuePartChip.value;
  if (!combinedChip) return false;
  const venueParts = store.state.filters.venueParts.value;
  return combinedChip.values.every((value) => venueParts.includes(value));
});

const isCoveredVenuePartChip = (chip) => {
  return chip.type === 'venue_part' &&
    chip.values.length === 1 &&
    (combinedVenuePartChip.value?.values.includes(chip.values[0]) ?? false);
};

const sortedChips = computed(() => {
  const selectedChips = selectedChipList.value.map((value) => chips.value.find((chip) => chip.value === value));
  const unselectedChips = chips.value.filter((chip) => !selectedChips.includes(chip) &&
    !(isCoveredVenuePartChip(chip) && combinedFullySelected.value));
  return [...selectedChips, ...unselectedChips].filter(Boolean);
});

const syncVenuePartChipsWithFilters = () => {
  const venueParts = store.state.filters.venueParts.value;
  const combinedChip = combinedVenuePartChip.value;
  const fullySelected = combinedFullySelected.value;

  chips.value.forEach((chip) => {
    if (chip.type !== 'venue_part') return;
    let shouldBeSelected;
    if (chip.value === combinedChip?.value) {
      // The combined chip follows its primary area type only, regardless of its siblings.
      shouldBeSelected = venueParts.includes(combinedPrimaryValue.value);
    } else if (isCoveredVenuePartChip(chip)) {
      // Sibling chips track their own area type, but stay hidden while the combined chip covers them.
      shouldBeSelected = venueParts.includes(chip.values[0]) && !fullySelected;
    } else {
      shouldBeSelected = venueParts.includes(chip.values[0]);
    }
    if (shouldBeSelected && !selectedChipList.value.includes(chip.value)) {
      selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])];
    } else if (!shouldBeSelected && selectedChipList.value.includes(chip.value)) {
      selectedChipList.value = selectedChipList.value.filter((value) => value !== chip.value);
    }
  });
};

const addFilter = (chipData) => {
  switch (chipData.type) {
    case 'venue_type':
      if (!Number.isInteger(+chipData.values[0])) {
        store.commit('setAdvancedFilter', { section: 'customRoomFeatures', value: [...new Set([...chipData.values, ...store.state.filters.customRoomFeatures.value])] });
      } else {
        store.commit('setAdvancedFilter', { section: 'venueTypes', value: [...new Set([...chipData.values.map(item => +item), ...store.state.filters.venueTypes.value])] });
      }
      break;
    case 'room_layout':
      store.commit('setAdvancedFilter', { section: 'layout', value: chipData.values.map(item => +item)[0] });
      break;
    case 'catering_options':
      store.commit('setAdvancedFilter', { section: 'catering', value: [...new Set([...chipData.values, ...store.state.filters.catering.value])] });
      break;
    case 'venue_part':
      store.commit('setVenueParts', [...new Set([...chipData.values, ...store.state.filters.venueParts.value])]);
      break;
    case 'venueFeatures':
      store.commit('setAdvancedFilter', { section: 'venueFeatures', value: [...new Set([...chipData.values, ...store.state.filters.venueFeatures.value])] });
      break;
    case 'room_features':
      if (!Number.isInteger(+chipData.values[0])) {
        store.commit('setAdvancedFilter', { section: 'customRoomFeatures', value: [...new Set([...chipData.values, ...store.state.filters.customRoomFeatures.value])] });
      } else {
        store.commit('setAdvancedFilter', { section: 'roomFeatures', value: [...new Set([...chipData.values, ...store.state.filters.roomFeatures.value])] });
      }
      break;
    case 'allowed_events':
      store.commit('setAdvancedFilter', { section: 'allowedEvents', value: [...new Set([...chipData.values, ...store.state.filters.allowedEvents.value])] });
      break;
    case 'price_range':
      store.commit('setAdvancedFilter', { section: 'customPriceRange', value: chipData.values[0] });
      break;
    case 'room_tags':
      // Normalize to numbers: URL-hydrated values are strings, chip values are numbers,
      // and Set would keep 103 and '103' as duplicates.
      store.commit('setAdvancedFilter', { section: 'roomTags', value: [...new Set([...chipData.values, ...store.state.filters.roomTags.value].map(Number))] });
      break;
    default:
  }
};

const removeFilter = (chipData) => {
  switch (chipData.type) {
    case 'venue_type':
      if (!Number.isInteger(+chipData.values[0])) {
        store.commit('setAdvancedFilter', { section: 'customRoomFeatures', value: store.state.filters.customRoomFeatures.value.filter(val => { return !chipData.values.includes(val); }) });
      } else {
        store.commit('setAdvancedFilter', { section: 'venueTypes', value: store.state.filters.venueTypes.value.filter(val => { return !chipData.values.includes(val.toString()); }) });
      }
      break;
    case 'room_layout': {
      const currentLayout = store.state.filters.layout.value;
      const chipLayoutValue = chipData.values.map(v => +v)[0];
      if (!currentLayout || currentLayout === chipLayoutValue) {
        store.commit('setAdvancedFilter', { section: 'layout', value: 0 });
      }
      break;
    }
    case 'catering_options':
      store.commit('setAdvancedFilter', { section: 'catering', value: store.state.filters.catering.value.filter(val => { return !chipData.values.includes(val); }) });
      break;
    case 'venue_part':
      store.commit('setVenueParts', store.state.filters.venueParts.value.filter(val => { return !chipData.values.includes(val); }));
      break;
    case 'venueFeatures':
      store.commit('setAdvancedFilter', { section: 'venueFeatures', value: store.state.filters.venueFeatures.value.filter(val => { return !chipData.values.includes(val); }) });
      break;
    case 'room_features':
      if (!Number.isInteger(+chipData.values[0])) {
        store.commit('setAdvancedFilter', { section: 'customRoomFeatures', value: store.state.filters.customRoomFeatures.value.filter(val => { return !chipData.values.includes(val); }) });
      } else {
        store.commit('setAdvancedFilter', { section: 'roomFeatures', value: store.state.filters.roomFeatures.value.filter(val => { return !chipData.values.includes(val); }) });
      }
      break;
    case 'allowed_events':
      store.commit('setAdvancedFilter', { section: 'allowedEvents', value: store.state.filters.allowedEvents.value.filter(val => { return !chipData.values.includes(val.toString()); }) });
      break;
    case 'price_range':
      if (chipData.values[0] === 'affordable' && selectedChipList.value.includes('upscale')) {
        store.commit('setAdvancedFilter', { section: 'customPriceRange', value: 'upscale' });
      } else if (chipData.values[0] === 'upscale' && selectedChipList.value.includes('affordable')) {
        store.commit('setAdvancedFilter', { section: 'customPriceRange', value: 'affordable' });
      } else {
        store.commit('setAdvancedFilter', { section: 'customPriceRange', value: null });
      }
      break;
    case 'room_tags':
      // Numeric comparison so URL-hydrated string values ('103') are removed too.
      store.commit('setAdvancedFilter', { section: 'roomTags', value: store.state.filters.roomTags.value.filter(val => { return +val !== +chipData.values[0]; }) });
      break;
    default:
  }
};

const onChangeChip = (chip, isSelected) => {
  updatedByChip.value = true;

  if (isSelected) {
    const chipData = chips.value.find(e => e.value === chip);
    if (chipData?.type === 'venue_part') {
      selectedChipList.value = selectedChipList.value.filter((v) => {
        const c = chips.value.find(e => e.value === v);
        return !(c?.type === 'venue_part' && v !== chip);
      });
    }
  }

  store.dispatch('trackGA4Interaction', {
    event: 'search_page_filters_engagement',
    section: 'search_listing_navigation',
    element: 'exp_chip_filter',
    elementValue: chip,
    interaction: isSelected ? 'select' : 'unselect',
  });

  if (isSelected) {
    store.commit('setSelectedFiltersReactive', true);
  }
};

watch(() => chips.value, (newChips, oldChips) => {
  chipsList.value = !oldChips ? newChips : [...new Set([...newChips, ...oldChips])];
}, { immediate: true });

watch(() => chips.value, (newChips) => {
  selectedChipList.value = selectedChipList.value.filter((chipValue) => {
    const found = newChips.find((chip) => {
      return chip.value === chipValue;
    });
    return found !== undefined;
  });

  // When quickFilters update, sync chip selection with any active venueParts
  syncVenuePartChipsWithFilters();
});

watch(() => store.state.filters.catering.value, (newFilters, oldFilters = []) => {
  updatedByChip.value = false;
  newFilters.forEach((filterId) => {
    if (!oldFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'catering_options' && e.values.includes(filterId)));
      chip && (selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])]);
    }
  });
  oldFilters.forEach((filterId) => {
    if (!newFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'catering_options' && e.values.includes(filterId)));
      chip && (selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value));
    }
  });
}, { immediate: true });

watch(() => store.state.filters.layout.value, (newFilter, oldFilter) => {
  updatedByChip.value = false;
  if (newFilter === oldFilter) return;
  if (newFilter === null || newFilter === 0) {
    const roomLayoutChips = chips.value.filter(e => (e.type !== 'room_layout')).map(e => e.value);
    selectedChipList.value = selectedChipList.value.filter(item => roomLayoutChips.includes(item));
    return;
  }
  chips.value.forEach((e) => {
    if (e.type === 'room_layout') {
      selectedChipList.value = selectedChipList.value.filter(item => item !== e.value);
    }
  });
  const chip = chips.value.find(e => (e.type === 'room_layout' && e.values.includes(newFilter.toString())));
  if (chip) {
    selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])];
  }
});

watch(() => store.state.filters.roomFeatures.value, (newFilters, oldFilters = []) => {
  updatedByChip.value = false;
  newFilters.forEach((filterId) => {
    if (!oldFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'room_features' && e.values.includes(filterId)));
      chip && (selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])]);
    }
  });
  oldFilters.forEach((filterId) => {
    if (!newFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'room_features' && e.values.includes(filterId)));
      chip && (selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value));
    }
  });
}, { immediate: true });

watch(() => store.state.filters.venueFeatures.value, (newFilters, oldFilters = []) => {
  updatedByChip.value = false;
  newFilters.forEach((filterId) => {
    if (!oldFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'venueFeatures' && e.values.includes(filterId)));
      chip && (selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])]);
    }
  });
  oldFilters.forEach((filterId) => {
    if (!newFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'venueFeatures' && e.values.includes(filterId)));
      chip && (selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value));
    }
  });
}, { immediate: true });

watch(() => store.state.filters.customRoomFeatures.value, (newFilters, oldFilters) => {
  updatedByChip.value = false;
  newFilters.forEach((filterId) => {
    if (!oldFilters.includes(filterId)) {
      const chip = chips.value.find(e => ((e.type === 'room_features' || e.type === 'venue_type') && e.values.includes(filterId)));
      chip && (selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])]);
    }
  });
  oldFilters.forEach((filterId) => {
    if (!newFilters.includes(filterId)) {
      const chip = chips.value.find(e => ((e.type === 'room_features' || e.type === 'venue_type') && e.values.includes(filterId)));
      chip && (selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value));
    }
  });
});

watch(() => store.state.filters.allowedEvents.value, (newFilters, oldFilters = []) => {
  updatedByChip.value = false;
  newFilters.forEach((filterId) => {
    if (!oldFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'allowed_events' && e.values.includes(filterId)));
      chip && (selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])]);
    }
  });
  oldFilters.forEach((filterId) => {
    if (!newFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'allowed_events' && e.values.includes(filterId)));
      chip && (selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value));
    }
  });
}, { immediate: true });

watch(() => store.state.filters.venueParts.value, () => {
  updatedByChip.value = false;
  syncVenuePartChipsWithFilters();
}, { immediate: true });

watch(() => store.state.filters.venueTypes.value, (newFilters, oldFilters = []) => {
  updatedByChip.value = false;
  newFilters.forEach((filterId) => {
    filterId = parseInt(filterId, 10);
    if (!oldFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'venue_type' && e.values.includes(filterId.toString())));
      if (chip?.values?.length === 1) {
        chip && (selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])]);
      }
    }
  });
  oldFilters.forEach((filterId) => {
    filterId = parseInt(filterId, 10);
    if (!newFilters.includes(filterId)) {
      const chip = chips.value.find(e => (e.type === 'venue_type' && e.values.includes(filterId.toString())));
      if (chip?.values?.length === 1) {
        chip && (selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value));
      }
    }
  });
}, { immediate: true });

watch([() => store.state.filters.roomTags.value, chips], ([newFilters, availableChips], [oldFilters = []]) => {
  if (!availableChips || availableChips.length === 0) {
    return;
  }
  updatedByChip.value = false;

  newFilters.forEach((filterId) => {
    if (!oldFilters.includes(filterId)) {
      const chip = availableChips.find(e => {
        return e.type === 'room_tags' && (e.values.includes(filterId) || e.values.includes(parseInt(filterId, 10)) || e.values.includes(filterId.toString()));
      });
      if (chip?.value && !selectedChipList.value.includes(chip.value)) {
        selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])];
      }
    }
  });
  oldFilters.forEach((filterId) => {
    if (!newFilters.includes(filterId)) {
      const chip = availableChips.find(e => e.type === 'room_tags' && (e.values.includes(filterId) || e.values.includes(parseInt(filterId, 10)) || e.values.includes(filterId.toString())));
      if (chip?.value) {
        selectedChipList.value = selectedChipList.value.filter(e => e !== chip.value);
      }
    }
  });
}, { immediate: true });

watch(() => store.state.filters.customPriceRange.value, (newFilter, oldFilter) => {
  updatedByChip.value = false;

  if (newFilter === oldFilter && oldFilter !== undefined) {
    return;
  }

  if (newFilter === null || newFilter === undefined) {
    const priceRangeChips = chips.value.filter(e => (e.type !== 'price_range')).map(e => e.value);
    selectedChipList.value = selectedChipList.value.filter(item => priceRangeChips.includes(item));
    return;
  }

  const chip = chips.value.find(e => {
    return e.type === 'price_range' && (e.values.includes(newFilter) || e.values.includes(newFilter.toString()));
  });

  if (chip?.value) {
    if (oldFilter && oldFilter !== newFilter) {
      selectedChipList.value = selectedChipList.value.filter(item => item !== oldFilter);
    }
    if (!selectedChipList.value.includes(chip.value)) {
      selectedChipList.value = [...new Set([...selectedChipList.value, chip.value])];
    }
  }
}, { immediate: true });

const scrollToZero = () => {
  chipsElement.value.$el.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

const scroll = (direction = 1) => {
  chipsElement.value.$el.scrollBy({
    top: 0,
    left: 220 * direction,
    behavior: 'smooth',
  });
};

// Carried chips arrive selected: their filters already came through the URL.
selectedChipList.value = [
  ...new Set([...store.state.carriedQuickFilters.map(toChipValue), ...selectedChipList.value]),
];

watch(activeEventTypeChipValue, (value) => {
  if (value && !selectedChipList.value.includes(value)) {
    selectedChipList.value = [value, ...selectedChipList.value];
  }
}, { immediate: true });

watch(selectedChipList, (newList, oldList) => {
  newList.forEach((chip) => {
    if (!oldList.includes(chip)) {
      const chipData = chips.value.find(e => e.value === chip);
      if (chipData === undefined || chipData.value === activeEventTypeChipValue.value) return;
      const eventTypeSlug = eventTypeChipSlug(chipData, store.state.l10n.ISOCountryCode);
      if (eventTypeSlug && eventTypeSlug !== store.state.eventTypeSlug && updatedByChip.value) {
        switchEventType(eventTypeSlug, newList.filter((value) => value !== chip));
        return;
      }
      // The combined chip broadens to all its area types only on an explicit tap. When it was
      // selected by sync (e.g. ticking just "Private indoor space" in the modal), the primary
      // area type is already applied — broadening here would wrongly add the sibling area types.
      const combinedChipSelectedBySync = chipData.value === combinedVenuePartChip.value?.value &&
        !updatedByChip.value;
      if (combinedChipSelectedBySync) return;
      scrollToZero();
      addFilter(chipData);
      if (updatedByChip.value) {
        store.dispatch('applyFilters');
      }
    }
  });
  oldList.forEach((chip) => {
    if (!newList.includes(chip)) {
      const chipData = chipsList.value.find(e => e.value === chip);
      if (chipData === undefined) return;
      if (chipData.value === activeEventTypeChipValue.value) {
        if (updatedByChip.value) {
          switchEventType(store.state.fromRoomTagSlug, newList);
        }
        return;
      }
      dropCarriedChip(chip);
      // A sibling chip removed only because the combined chip now fully covers it must keep its
      // underlying area-type filter — don't strip it from the modal selection.
      const coveredByCombinedChip = isCoveredVenuePartChip(chipData) && combinedFullySelected.value;
      const combinedChipDeselectedBySync = chipData.value === combinedVenuePartChip.value?.value &&
        !updatedByChip.value;
      if (coveredByCombinedChip || combinedChipDeselectedBySync) return;
      removeFilter(chipData);
      if (updatedByChip.value) {
        store.dispatch('applyFilters');
      }
    }
  });
});

const addDragScrollOnDesktop = () => {
  // @see https://stackoverflow.com/a/77104729
  const elem = chipsElement.value.$el;
  let isDragging = false;
  let startX = 0;

  // Mouse only: a touch screen scrolls the strip natively, and capturing the finger as well had this code scroll it
  // a second time under the browser's own scrolling.
  const dragStart = (ev) => {
    if (ev.pointerType !== 'mouse') {
      return;
    }
    startX = ev.clientX;
    isDragging = false;
    elem.setPointerCapture(ev.pointerId);
  };

  const dragEnd = (ev) => {
    if (ev.pointerType !== 'mouse') {
      return;
    }
    elem.releasePointerCapture(ev.pointerId);
    // Prevent click if user was dragging
    if (isDragging) {
      ev.preventDefault();
      ev.stopPropagation();
    } else {
      const elementAtPoint = document.elementFromPoint(ev.clientX, ev.clientY);
      if (elementAtPoint) {
        // firefox needs special handling to trigger input click
        const siblingInput = elementAtPoint.closest('label')?.querySelector('input');
        if (siblingInput) {
          siblingInput.click();
        }
      }
    }
    isDragging = false;
  };

  const drag = (ev) => {
    if (elem.hasPointerCapture(ev.pointerId)) {
      const deltaX = Math.abs(ev.clientX - startX);
      // Consider it dragging if moved more than 5px
      if (deltaX > 5) {
        isDragging = true;
        elem.scrollLeft -= ev.movementX;
      }
    }
  };
  useEventListener(elem, 'pointerdown', dragStart);
  useEventListener(elem, 'pointerup', dragEnd);
  useEventListener(elem, 'pointermove', drag);
};

onMounted(() => {
  if (sortedChips.value.length === 0) return;

  nextTick(() => {
    useIntersectionObserver(
      firstChip,
      ([entry]) => {
        firstChipIsVisible.value = entry?.isIntersecting || false;
      },
    );
    useIntersectionObserver(
      lastChip,
      ([entry]) => {
        lastChipIsVisible.value = entry?.isIntersecting || false;
      },
    );
    addDragScrollOnDesktop();
  }, 100);

  store.commit('setExperimentChipsFiltersWasMounted', true);
});
</script>
